import { AnimatePresence, motion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';
import React, { useMemo, useState } from 'react';

export type SimpleSeriesPoint = {
  date: string; // YYYY-MM-01 or ISO date
  value: number | string;
};

type Props = {
  title: string;
  series: SimpleSeriesPoint[];
  valueFormatter?: (n: number) => string;
};

const formatMonth = (dateStr: string) => {
  // Avoid timezone shifts by formatting using the literal YYYY-MM prefix.
  const ym = (dateStr || '').slice(0, 7); // supports YYYY-MM-01 or YYYY-MM
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!m) return ym || dateStr;
  const year = m[1];
  const month = parseInt(m[2], 10);
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${monthNames[month - 1] ?? m[2]} ${year}`;
};

export const SimpleValueLineChart: React.FC<Props> = ({ title, series, valueFormatter }) => {
  const [hovered, setHovered] = useState<{
    x: number;
    y: number;
    date: string;
    value: number;
  } | null>(null);

  const points = useMemo(() => {
    const cleaned = (series ?? [])
      .map(p => ({
        date: p?.date,
        value: typeof p?.value === 'number' ? p.value : parseFloat(String(p?.value)),
      }))
      .filter(p => !!p?.date && Number.isFinite(p.value));
    const max = Math.max(...cleaned.map(p => p.value), 0);
    const min = Math.min(...cleaned.map(p => p.value), 0);
    return { cleaned, max, min };
  }, [series]);

  if (!points.cleaned.length) {
    return (
      <div className="flex items-center justify-center h-56 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600">
        <div className="text-center">
          <TrendingUp className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400 text-sm">No data yet</p>
        </div>
      </div>
    );
  }

  // Dynamic width to keep points reasonably close and avoid unnecessary scrolling
  const minPointSpacing = 90;
  const padding = { top: 20, right: 20, bottom: 50, left: 60 };
  const width = Math.max(
    560,
    padding.left + padding.right + points.cleaned.length * minPointSpacing
  );
  const height = 260;
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const range = points.max - points.min || 1;
  const y = (v: number) => padding.top + innerH - ((v - points.min) / range) * innerH;
  const x = (i: number) =>
    padding.left +
    (points.cleaned.length === 1 ? innerW / 2 : (i / (points.cleaned.length - 1)) * innerW);

  const path = points.cleaned
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.value)}`)
    .join(' ');

  const fmt = valueFormatter ?? (n => String(n));
  const labelStep = points.cleaned.length <= 8 ? 1 : points.cleaned.length <= 16 ? 2 : 3;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/30 dark:to-emerald-800/20 rounded-xl border border-emerald-200 dark:border-emerald-800/30">
          <TrendingUp
            className="w-5 h-5 text-emerald-700 dark:text-emerald-300"
            strokeWidth={2.5}
          />
        </div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h3>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-lg overflow-x-auto">
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          style={{ minWidth: `${width}px` }}
        >
          {/* y axis labels (min / mid / max) */}
          {[0, 0.5, 1].map((ratio, idx) => {
            const v = points.min + range * ratio;
            const yy = y(v);
            return (
              <g key={`y-${idx}`}>
                <line
                  x1={padding.left}
                  y1={yy}
                  x2={width - padding.right}
                  y2={yy}
                  stroke="currentColor"
                  strokeWidth={1}
                  strokeDasharray="4 4"
                  className="text-gray-200 dark:text-gray-700"
                />
                <text
                  x={padding.left - 10}
                  y={yy + 4}
                  textAnchor="end"
                  className="text-xs font-semibold fill-gray-600 dark:fill-gray-400"
                >
                  {fmt(Number(v.toFixed(2)))}
                </text>
              </g>
            );
          })}

          {/* path */}
          <motion.path
            d={path}
            fill="none"
            stroke="#059669"
            strokeWidth={3}
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.9, ease: 'easeInOut' }}
          />

          {/* points + x labels */}
          {points.cleaned.map((p, i) => (
            <g key={`p-${i}`}>
              {/* Hover hit area */}
              <circle
                cx={x(i)}
                cy={y(p.value)}
                r={16}
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={() =>
                  setHovered({
                    x: x(i),
                    y: y(p.value),
                    date: p.date,
                    value: p.value,
                  })
                }
                onMouseLeave={() => setHovered(null)}
              />
              <circle
                cx={x(i)}
                cy={y(p.value)}
                r={5}
                fill="#059669"
                stroke="white"
                strokeWidth={2}
              />
              {i % labelStep === 0 || i === points.cleaned.length - 1 ? (
                <text
                  x={x(i)}
                  y={height - 20}
                  textAnchor="middle"
                  className="text-xs font-semibold fill-gray-700 dark:fill-gray-300"
                >
                  {formatMonth(p.date)}
                </text>
              ) : null}
            </g>
          ))}

          {/* Tooltip */}
          <AnimatePresence>
            {hovered && (
              <motion.g
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                style={{ pointerEvents: 'none' }}
              >
                {(() => {
                  const boxW = 170;
                  const boxH = 56;
                  const pad = 10;
                  const xPos = Math.min(
                    Math.max(hovered.x - boxW / 2, padding.left),
                    width - padding.right - boxW
                  );
                  const yPos = Math.max(hovered.y - boxH - 14, padding.top);
                  return (
                    <>
                      <rect
                        x={xPos}
                        y={yPos}
                        width={boxW}
                        height={boxH}
                        rx={10}
                        fill="rgba(15, 23, 42, 0.95)"
                        stroke="rgba(16, 185, 129, 0.45)"
                        strokeWidth={2}
                      />
                      <text
                        x={xPos + pad}
                        y={yPos + 22}
                        className="text-xs font-semibold fill-gray-200"
                      >
                        {formatMonth(hovered.date)}
                      </text>
                      <text x={xPos + pad} y={yPos + 44} className="text-sm font-black fill-white">
                        {fmt(hovered.value)}
                      </text>
                    </>
                  );
                })()}
              </motion.g>
            )}
          </AnimatePresence>
        </svg>
      </div>
    </motion.div>
  );
};
